import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productAPI, categoryAPI, uploadAPI } from '../../utils/api';
import { Plus, Edit2, Trash2, Search, X, Upload, FolderPlus } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = { name:'', category_id:'', short_desc:'', description:'', price:'', compare_price:'', sku:'', stock:'', hair_type:'', size:'', images:[], ingredients:'', how_to_use:'', is_featured:false, is_active:true };
const EMPTY_CAT = { name:'', description:'' };

export default function AdminProductsPage() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newImgUrl, setNewImgUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState(EMPTY_CAT);
  const [tab, setTab] = useState('products');

  const { data: products = [], isLoading } = useQuery({ queryKey: ['admin-products'], queryFn: () => productAPI.adminGetAll().then(r => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => categoryAPI.getAll().then(r => r.data) });

  // Product mutations
  const saveMut = useMutation({
    mutationFn: (data) => editing ? productAPI.update(editing.id, data) : productAPI.create(data),
    onSuccess: () => { toast.success(editing ? 'Produkti u përditësua!' : 'Produkti u krijua!'); qc.invalidateQueries(['admin-products']); setShowForm(false); setEditing(null); setForm(EMPTY_FORM); },
    onError: (e) => toast.error(e.response?.data?.error || 'Gabim'),
  });
  const deleteMut = useMutation({
    mutationFn: productAPI.remove,
    onSuccess: () => { toast.success('Produkti u çaktivizua'); qc.invalidateQueries(['admin-products']); },
  });

  // Category mutations
  const saveCatMut = useMutation({
    mutationFn: (data) => editingCat ? categoryAPI.update(editingCat.id, data) : categoryAPI.create(data),
    onSuccess: () => { toast.success(editingCat ? 'Kategoria u përditësua!' : 'Kategoria u krijua!'); qc.invalidateQueries(['categories']); setShowCatForm(false); setEditingCat(null); setCatForm(EMPTY_CAT); },
    onError: (e) => toast.error(e.response?.data?.error || 'Gabim'),
  });
  const deleteCatMut = useMutation({
    mutationFn: categoryAPI.remove,
    onSuccess: () => { toast.success('Kategoria u fshi'); qc.invalidateQueries(['categories']); },
  });

  const openEdit = (p) => {
    setEditing(p);
    const imgs = (() => { try { return JSON.parse(p.images) || []; } catch { return p.images ? [p.images] : []; } })();
    setForm({ ...p, images: imgs });
    setShowForm(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadAPI.image(file);
      setForm(f => ({ ...f, images: [...f.images, data.url] }));
      toast.success('Imazhi u ngarkua!');
    } catch { toast.error('Gabim gjatë ngarkimit'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const addImgUrl = () => {
    const url = newImgUrl.trim();
    if (!url) return;
    setForm(f => ({ ...f, images: [...f.images, url] }));
    setNewImgUrl('');
  };

  const removeImg = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMut.mutate({ ...form, price: +form.price, compare_price: form.compare_price ? +form.compare_price : null, stock: +form.stock });
  };

  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').includes(search));

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:'1.6rem', marginBottom:4 }}>Produktet & Kategoritë</h1>
          <p style={{ color:'var(--text-light)', fontSize:'.88rem' }}>{products.length} produkte · {categories.length} kategori</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-outline btn-sm" onClick={() => { setEditingCat(null); setCatForm(EMPTY_CAT); setShowCatForm(true); }}>
            <FolderPlus size={15} /> Kategori e Re
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }}>
            <Plus size={16} /> Produkt i Ri
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'white', borderRadius:'var(--radius-md)', padding:4, border:'1px solid var(--cream-dark)', width:'fit-content', marginBottom:20 }}>
        {[['products','Produktet'],['categories','Kategoritë']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding:'7px 18px', borderRadius:'var(--radius-sm)', border:'none', cursor:'pointer', fontSize:'.82rem', fontWeight:500, background: tab===k ? 'var(--green-deep)' : 'transparent', color: tab===k ? 'white' : 'var(--text-light)', transition:'all .2s' }}>{l}</button>
        ))}
      </div>

      {/* PRODUCTS TAB */}
      {tab === 'products' && <>
        <div className="search-bar">
          <Search size={16} />
          <input className="form-input" placeholder="Kërko produkt ose SKU..." value={search} onChange={e => setSearch(e.target.value)} style={{ border:'none',outline:'none',flex:1,padding:'8px 0',background:'transparent' }} />
          {search && <button onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Produkti</th><th>SKU</th><th>Kategoria</th><th>Çmimi</th><th>Stoku</th><th>Statusi</th><th>Veprime</th></tr></thead>
              <tbody>
                {isLoading && <tr><td colSpan={7} style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>}
                {filtered.map(p => {
                  const imgs = (() => { try { return JSON.parse(p.images) || []; } catch { return []; } })();
                  return (
                    <tr key={p.id}>
                      <td><div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        {imgs[0] && <img src={imgs[0]} alt="" style={{ width:44, height:50, objectFit:'contain', borderRadius:8, background:'var(--cream)' }} />}
                        <div>
                          <div style={{ fontWeight:600, fontSize:'.88rem' }}>{p.name}</div>
                          <div style={{ fontSize:'.75rem', color:'var(--text-light)' }}>{p.size}</div>
                        </div>
                      </div></td>
                      <td><code style={{ fontSize:'.78rem', background:'var(--cream)', padding:'2px 8px', borderRadius:4 }}>{p.sku}</code></td>
                      <td>{p.category_name || '—'}</td>
                      <td><div style={{ fontWeight:700 }}>${p.price}</div>{p.compare_price && <div style={{ fontSize:'.75rem', color:'var(--text-light)', textDecoration:'line-through' }}>${p.compare_price}</div>}</td>
                      <td><span className={`badge ${p.stock===0?'badge-red':p.stock<=p.low_stock_threshold?'badge-gold':'badge-green'}`}>{p.stock}</span></td>
                      <td><span className={`badge ${p.is_active?'badge-green':'badge-gray'}`}>{p.is_active?'Aktiv':'Çaktiv'}</span></td>
                      <td><div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}><Edit2 size={14} /></button>
                        <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={() => { if(window.confirm('Çaktivizo?')) deleteMut.mutate(p.id); }}><Trash2 size={14} /></button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>}

      {/* CATEGORIES TAB */}
      {tab === 'categories' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Emri</th><th>Slug</th><th>Përshkrimi</th><th>Produktet</th><th>Veprime</th></tr></thead>
              <tbody>
                {categories.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight:600 }}>{c.name}</td>
                    <td><code style={{ fontSize:'.78rem', background:'var(--cream)', padding:'2px 8px', borderRadius:4 }}>{c.slug}</code></td>
                    <td style={{ color:'var(--text-light)', fontSize:'.85rem' }}>{c.description || '—'}</td>
                    <td>{products.filter(p => p.category_name === c.name).length}</td>
                    <td><div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditingCat(c); setCatForm({ name:c.name, description:c.description||'' }); setShowCatForm(true); }}><Edit2 size={14} /></button>
                      <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={() => { if(window.confirm('Fshi kategorinë?')) deleteCatMut.mutate(c.id); }}><Trash2 size={14} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCatForm && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowCatForm(false)}>
          <div className="modal-box" style={{ maxWidth:480 }}>
            <div className="modal-header">
              <h2 style={{ fontSize:'1.1rem' }}>{editingCat ? 'Ndrysho Kategorinë' : 'Kategori e Re'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCatForm(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">Emri *</label>
                <input className="form-input" required value={catForm.name} onChange={e => setCatForm({...catForm, name:e.target.value})} />
              </div>
              <div className="form-group"><label className="form-label">Përshkrimi</label>
                <textarea className="form-input" rows={3} value={catForm.description} onChange={e => setCatForm({...catForm, description:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowCatForm(false)}>Anulo</button>
              <button className="btn btn-primary" disabled={!catForm.name || saveCatMut.isPending} onClick={() => saveCatMut.mutate(catForm)}>
                {saveCatMut.isPending ? 'Duke ruajtur...' : editingCat ? 'Ruaj' : 'Krijo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowForm(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2 style={{ fontSize:'1.2rem' }}>{editing ? 'Ndrysho Produktin' : 'Produkt i Ri'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-row-2">
                <div className="form-group"><label className="form-label">Emri *</label><input className="form-input" required value={form.name} onChange={e => setForm({...form,name:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Kategoria</label>
                  <select className="form-input" value={form.category_id} onChange={e => setForm({...form,category_id:e.target.value})}>
                    <option value="">Zgjidh kategorinë...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Përshkrim i shkurtër</label><input className="form-input" value={form.short_desc} onChange={e => setForm({...form,short_desc:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Përshkrim i plotë</label><textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({...form,description:e.target.value})} /></div>
              <div className="form-row-3">
                <div className="form-group"><label className="form-label">Çmimi *</label><input className="form-input" type="number" step="0.01" required value={form.price} onChange={e => setForm({...form,price:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Çmimi i vjetër</label><input className="form-input" type="number" step="0.01" value={form.compare_price} onChange={e => setForm({...form,compare_price:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Stoku</label><input className="form-input" type="number" value={form.stock} onChange={e => setForm({...form,stock:e.target.value})} /></div>
              </div>
              <div className="form-row-2">
                <div className="form-group"><label className="form-label">SKU</label><input className="form-input" value={form.sku} onChange={e => setForm({...form,sku:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Madhësia</label><input className="form-input" value={form.size} onChange={e => setForm({...form,size:e.target.value})} /></div>
              </div>
              <div className="form-group"><label className="form-label">Lloji i flokut</label><input className="form-input" value={form.hair_type} onChange={e => setForm({...form,hair_type:e.target.value})} placeholder="p.sh. Të gjitha llojet" /></div>

              {/* IMAGE SECTION */}
              <div className="form-group">
                <label className="form-label">Imazhet e Produktit</label>
                {/* Current images */}
                {form.images.length > 0 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                    {form.images.map((img, i) => (
                      <div key={i} style={{ position:'relative' }}>
                        <img src={img} alt="" style={{ width:80, height:88, objectFit:'contain', borderRadius:8, background:'var(--cream)', border:'1.5px solid var(--cream-dark)' }} />
                        <button type="button" onClick={() => removeImg(i)} style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'var(--danger)', color:'white', border:'none', cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Upload file */}
                <div style={{ display:'flex', gap:10, marginBottom:8 }}>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFileUpload} />
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
                    <Upload size={14} /> {uploading ? 'Duke ngarkuar...' : 'Ngarko Imazh'}
                  </button>
                </div>
                {/* URL input */}
                <div style={{ display:'flex', gap:8 }}>
                  <input className="form-input" placeholder="ose shto URL-in e imazhit..." value={newImgUrl} onChange={e => setNewImgUrl(e.target.value)} onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addImgUrl())} />
                  <button type="button" className="btn btn-outline btn-sm" onClick={addImgUrl}>+ Shto</button>
                </div>
              </div>

              <div className="form-group"><label className="form-label">Përbërësit</label><textarea className="form-input" rows={2} value={form.ingredients} onChange={e => setForm({...form,ingredients:e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Si të Përdoret</label><textarea className="form-input" rows={2} value={form.how_to_use} onChange={e => setForm({...form,how_to_use:e.target.value})} /></div>
              <div style={{ display:'flex', gap:20, margin:'8px 0 4px' }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'.88rem' }}>
                  <input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form,is_featured:e.target.checked})} /> Bestseller
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'.88rem' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form,is_active:e.target.checked})} /> Aktiv
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Anulo</button>
                <button type="submit" className="btn btn-primary" disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Duke ruajtur...' : editing ? 'Ruaj Ndryshimet' : 'Krijo Produktin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .search-bar { display:flex;align-items:center;gap:10px;background:white;border:1.5px solid var(--cream-dark);border-radius:var(--radius-md);padding:6px 14px;margin-bottom:20px;color:var(--text-light); }
        .modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px); }
        .modal-box { background:white;border-radius:var(--radius-xl);width:min(720px,100%);max-height:90vh;display:flex;flex-direction:column;animation:modalIn .2s ease; }
        @keyframes modalIn { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        .modal-header { display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid var(--cream-dark); }
        .modal-body { padding:24px;overflow-y:auto; }
        .modal-footer { display:flex;justify-content:flex-end;gap:12px;margin-top:20px;padding-top:16px;border-top:1px solid var(--cream-dark); }
        .form-row-2 { display:grid;grid-template-columns:1fr 1fr;gap:16px; }
        .form-row-3 { display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px; }
      `}</style>
    </div>
  );
}
